/**
 * Desk "add a customer" API wrapper (DNKADM-0413, over DNKAPI-0583).
 *
 * `POST /v2/admin/customers` runs the walk-in sale's own create-or-find + auto-invite steps on
 * their own: no order, no booking, no charge. The body IS the contact (`firstName`, `lastName`,
 * `email` required; `phone` optional), the same shape as {@link WalkInContactInput} in
 * `walkInSale.ts`. The tenant comes from the JWT server-side; nothing tenant- or GHL-scoped is
 * sent. The response is `201 {contactId, invited, existing, firstName, lastName}`. The console then
 * books on behalf of `contactId` through the normal flow.
 *
 * `firstName`/`lastName` (DNKAPI-0584, DNKADM-0414) are the contact's STORED names — the kept name
 * on a match, otherwise the typed name the contact was created with. They are optional: an older
 * server (before DNKAPI-0584) omits them, and a blank or non-string value is read as absent
 * (`null`), the same reading as the walk-in sale's `matchedFirstName`/`matchedLastName`.
 *
 * `existing` is a HINT for the desk's notice copy only (Operator rule, 2026-09-24). The server can
 * report `false` for a real existing customer (a 60s roster cache, DNKAPI-0583 Note 8), so no
 * caller may branch behavior on it.
 *
 * Bearer-only `/v2/admin/**` surface, routed and audited like every other v2 admin call
 * (`request()`). Throws a typed `ApiError` on a non-2xx, mapped by {@link toAddCustomerErrorView}.
 */
import { request } from './client';
import { ApiError } from './errors';
import { v2ErrorCode } from './v2Errors';
import { serverMessage } from './serverMessage';
import type { WalkInContactInput } from './walkInSale';

const PATH = '/v2/admin/customers';

/** The customer to create or find. `firstName`/`lastName`/`email` are required. */
export type AddCustomerInput = WalkInContactInput;

export interface AddCustomerResult {
  /** The created or found contact; always present on a 201. */
  contactId: string;
  /** `true` when an invitation was created THIS call; `false` when the email already had access or a pending invite. */
  invited: boolean;
  /**
   * `true` when the email already belonged to a contact (its stored name was kept). A HINT for
   * notice copy only — never branch behavior on it (see the module doc).
   */
  existing: boolean;
  /** The stored first name (DNKAPI-0584); `null` when absent or blank (an older server). */
  firstName: string | null;
  /** The stored last name (DNKAPI-0584); `null` when absent or blank (an older server). */
  lastName: string | null;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function toBody(input: AddCustomerInput): Record<string, unknown> {
  const body: Record<string, unknown> = {
    firstName: input.firstName.trim(),
    lastName: input.lastName.trim(),
    email: input.email.trim(),
  };
  const phone = input.phone?.trim();
  if (phone) body['phone'] = phone;
  return body;
}

/**
 * `POST /v2/admin/customers` → create or find the contact by email and auto-invite it. Throws a
 * typed `ApiError` on a non-2xx: `400 INVALID_REQUEST` on a missing name/email, `409` on a genuine
 * invite conflict, `503 CONTACT_UNAVAILABLE` when the contact could not be provisioned. A 2xx
 * body with no usable `contactId` throws a plain `Error` (a failure, never a selection).
 */
export async function addCustomer(input: AddCustomerInput): Promise<AddCustomerResult> {
  const body = await request<unknown>('POST', PATH, { json: toBody(input) });
  const o = isObject(body) ? body : {};
  const contactId = asText(o['contactId']);
  if (contactId === null) {
    throw new Error('addCustomer: the 2xx response carried no contactId');
  }
  return {
    contactId,
    invited: o['invited'] === true,
    existing: o['existing'] === true,
    firstName: asText(o['firstName']),
    lastName: asText(o['lastName']),
  };
}

/**
 * The message the desk sees for an {@link addCustomer} failure: the server's own message
 * (brand-scrubbed) wherever it sent one, since the spec shows it inline. Branches on the envelope
 * `code`, never on message text, and appends the request id when present.
 */
export function toAddCustomerErrorView(error: unknown): string {
  if (!(error instanceof ApiError)) return 'The customer could not be added.';
  const message = serverMessage(error);
  const suffix = error.requestId ? ` Request ID: ${error.requestId}` : '';
  if (error.code === 'SESSION_EXPIRED') {
    return `Your session expired. Please sign in again to continue.${suffix}`;
  }
  if (v2ErrorCode(error) === 'CONTACT_UNAVAILABLE') {
    return `${message ?? "Couldn't set up this customer. Try again."}${suffix}`;
  }
  if (error.status === 400) {
    return `${message ?? 'Check the name and email, then try again.'}${suffix}`;
  }
  return `${message ?? 'The customer could not be added.'}${suffix}`;
}
